import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { IErrorModel, IPaginatedResponse } from '@core/models/generic.model';
import { IAs2Message } from '@core/models/as2-message.model';
import as2IntegrationService from 'services/api/as2-integration.service';

interface IAs2SliceState {
    loading: boolean;
    detailLoading: boolean;
    error: IErrorModel | undefined;
    messages: IPaginatedResponse<IAs2Message> | null;
    selected: IAs2Message | null;
}

const initialState: IAs2SliceState = {
    loading: false,
    detailLoading: false,
    error: {} as IErrorModel,
    messages: null,
    selected: null,
};

export const getAs2MessagesThunk = createAsyncThunk<
    IPaginatedResponse<IAs2Message>,
    { page: number; size: number; partner?: string; status?: string; direction?: string },
    { rejectValue: IErrorModel }
>('as2/getMessages', async (params, { rejectWithValue }) => {
    try {
        const response: any = await as2IntegrationService.getMessages(params);
        return response.data.as2Messages;
    } catch (err: any) {
        toast.error('Failed to load AS2 messages');
        return rejectWithValue(err);
    }
});

export const getAs2MessageThunk = createAsyncThunk<
    IAs2Message,
    number,
    { rejectValue: IErrorModel }
>('as2/getMessage', async (id, { rejectWithValue }) => {
    try {
        const response: any = await as2IntegrationService.getMessage(id);
        return response.data.as2Message;
    } catch (err: any) {
        toast.error('Failed to load AS2 message');
        return rejectWithValue(err);
    }
});

const as2IntegrationSlice = createSlice({
    name: 'as2Integration',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getAs2MessagesThunk.pending, (state) => {
                state.loading = true;
            })
            .addCase(getAs2MessagesThunk.fulfilled, (state, action) => {
                state.loading = false;
                state.messages = action.payload;
            })
            .addCase(getAs2MessagesThunk.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(getAs2MessageThunk.pending, (state) => {
                state.detailLoading = true;
                state.selected = null;
            })
            .addCase(getAs2MessageThunk.fulfilled, (state, action) => {
                state.detailLoading = false;
                state.selected = action.payload;
            })
            .addCase(getAs2MessageThunk.rejected, (state) => {
                state.detailLoading = false;
            });
    },
});

export default as2IntegrationSlice;
export const as2IntegrationReducer = as2IntegrationSlice.reducer;
